import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  addDays,
  addMonths,
  format,
  isBefore,
  isToday,
  subDays,
  subYears,
} from 'date-fns'
import { debounce } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AdminSitesDocument,
  OverrideSlotsDocument,
  SlotStatus,
  SlotsDocument,
} from '../gql/graphql'
import { useSiteContext } from '../hooks'
import EventDialog from './EventDialog'
import OrderDialog from './OrderDialog'

gql`
  query Slots($siteId: UUID!, $date: Date!) {
    slots(siteId: $siteId, date: $date) {
      courtId
      startTime
      price
      status
      reason
      orderId
      eventId
    }
  }
`

gql`
  mutation OverrideSlots($slots: [SlotOverrideInput!]!) {
    overrideSlots(slots: $slots) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

interface SlotMeta {
  price: string
  status: SlotStatus
  startTime: string
  reason?: string
  orderId?: string
  eventId?: string
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)

function formatHour(hour: number): string {
  return `${hour.toString().padStart(2, '0')}:00`
}

/** Extract {hour, courtId} from a mouse event target within the DataGrid */
function getCellInfo(target: HTMLElement): { hour: number; courtId: string } | null {
  const cell = target.closest('[data-field]') as HTMLElement | null
  const row = target.closest('[data-id]') as HTMLElement | null
  if (!cell || !row) return null
  const field = cell.dataset.field
  const rowId = row.dataset.id
  if (!field || field === 'hour' || rowId == null) return null
  return { hour: Number(rowId), courtId: field }
}

/** Compute the rectangle of cells between two corners */
function getCellRect(
  start: { hour: number; courtId: string },
  end: { hour: number; courtId: string },
  courtIds: string[],
) {
  const minHour = Math.min(start.hour, end.hour)
  const maxHour = Math.max(start.hour, end.hour)
  const si = courtIds.indexOf(start.courtId)
  const ei = courtIds.indexOf(end.courtId)
  if (si === -1 || ei === -1) return []
  const minCol = Math.min(si, ei)
  const maxCol = Math.max(si, ei)
  const cells: { hour: number; courtId: string }[] = []
  for (let h = minHour; h <= maxHour; h++) {
    for (let c = minCol; c <= maxCol; c++) {
      cells.push({ hour: h, courtId: courtIds[c] })
    }
  }
  return cells
}

const DRAG_HIGHLIGHT = 'cell-drag-highlight'

/** Current hour in China timezone (0-23) */
function getChinaHour() {
  return (new Date().getUTCHours() + 8) % 24
}

/** Today's date in China timezone as a Date (midnight local) */
function getChinaToday() {
  const now = new Date()
  const chinaMs = now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60_000
  const china = new Date(chinaMs)
  return new Date(china.getFullYear(), china.getMonth(), china.getDate())
}

const STATUS_LEGEND: {
  status: SlotStatus
  label: string
  bg: string
  color: string
}[] = [
  { status: SlotStatus.Open, label: '可预订', bg: '#fff', color: '#333' },
  { status: SlotStatus.Reserved, label: '已保留', bg: '#fff3e0', color: '#e65100' },
  { status: SlotStatus.Booked, label: '已预订', bg: '#e8f5e9', color: '#2e7d32' },
  { status: SlotStatus.Locked, label: '已锁定', bg: '#e0e0e0', color: '#424242' },
]

export default function SlotsPage() {
  const { currentSite } = useSiteContext()
  const [inputDate, setInputDate] = useState<Date | null>(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())

  const today = useMemo(() => new Date(), [])
  const minDate = subYears(today, 1)
  const maxDate = addMonths(today, 3)

  // Debounced setSelectedDate to avoid multiple queries on rapid clicks
  const debouncedSetSelectedDate = useMemo(
    () => debounce((date: Date | null) => setSelectedDate(date), 300),
    [],
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetSelectedDate.cancel()
    }
  }, [debouncedSetSelectedDate])

  const isPrevDayDisabled = (() => {
    const current = inputDate || selectedDate
    if (!current) return true
    return subDays(current, 1) < minDate
  })()

  const isNextDayDisabled = (() => {
    const current = inputDate || selectedDate
    if (!current) return true
    return addDays(current, 1) > maxDate
  })()

  const handleDateChange = (date: Date | null) => {
    setInputDate(date)
  }

  const handleDateAccept = (date: Date | null) => {
    // Only commit dates with year >= 2000 to avoid partial input issues
    if (date && !Number.isNaN(date.getTime()) && date.getFullYear() >= 2000) {
      setInputDate(date)
      setSelectedDate(date)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleDateAccept(inputDate)
    }
  }

  const handlePrevDay = () => {
    const current = inputDate || selectedDate
    if (current) {
      const prevDay = subDays(current, 1)
      if (prevDay >= minDate) {
        setInputDate(prevDay)
        debouncedSetSelectedDate(prevDay)
      }
    }
  }

  const handleNextDay = () => {
    const current = inputDate || selectedDate
    if (current) {
      const nextDay = addDays(current, 1)
      if (nextDay <= maxDate) {
        setInputDate(nextDay)
        debouncedSetSelectedDate(nextDay)
      }
    }
  }

  const handleTodayClick = () => {
    const today = new Date()
    setInputDate(today)
    debouncedSetSelectedDate(today)
  }

  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = useQuery(AdminSitesDocument, {
    fetchPolicy: 'network-only',
  })

  const {
    data: slotsData,
    loading: slotsLoading,
    error: slotsError,
    refetch: slotsRefetch,
  } = useQuery(SlotsDocument, {
    variables: {
      siteId: currentSite?.id as string,
      date:
        selectedDate && !Number.isNaN(selectedDate.getTime())
          ? format(selectedDate, 'yyyy-MM-dd')
          : '',
    },
    skip: !currentSite || !selectedDate || Number.isNaN(selectedDate.getTime()),
    fetchPolicy: 'no-cache',
  })

  const courts = useMemo(() => {
    if (!sitesData || !currentSite) return []
    const site = sitesData.adminSites.find((s) => s.id === currentSite.id)
    const courtsList = site?.courts ?? []
    return [...courtsList].sort((a, b) => a.displayOrder - b.displayOrder)
  }, [sitesData, currentSite])

  const slotLookup = useMemo(() => {
    const slots = slotsData?.slots ?? []
    const lookup = new Map<string, SlotMeta>()
    for (const s of slots) {
      const chinaHour = (new Date(s.startTime).getUTCHours() + 8) % 24
      lookup.set(`${chinaHour}-${s.courtId}`, {
        price: s.price ?? '',
        status: s.status,
        startTime: s.startTime,
        reason: s.reason ?? undefined,
        orderId: s.orderId ?? undefined,
        eventId: s.eventId ?? undefined,
      })
    }
    return lookup
  }, [slotsData])

  // For each court, assign a span-group key to consecutive hours sharing the same reason.
  // Cells without a reason get a unique key so they are never merged.
  const spanKeyMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const court of courts) {
      let groupCount = 0
      let prevReason: string | undefined
      let currentGroupKey = ''
      for (const hour of HOURS) {
        const key = `${hour}-${court.id}`
        const reason = slotLookup.get(key)?.reason
        if (reason !== undefined && reason === prevReason) {
          map.set(key, currentGroupKey)
        } else if (reason !== undefined) {
          groupCount++
          currentGroupKey = `span-${court.id}-${groupCount}`
          map.set(key, currentGroupKey)
          prevReason = reason
        } else {
          map.set(key, key) // unique → no spanning
          prevReason = undefined
          currentGroupKey = ''
        }
      }
    }
    return map
  }, [slotLookup, courts])

  // Reverse map: spanKey → all cells in that merged group
  const spanGroupCells = useMemo(() => {
    const map = new Map<string, { hour: number; courtId: string }[]>()
    for (const court of courts) {
      for (const hour of HOURS) {
        const key = `${hour}-${court.id}`
        const spanKey = spanKeyMap.get(key)
        if (!spanKey) continue
        if (!map.has(spanKey)) map.set(spanKey, [])
        map.get(spanKey)?.push({ hour, courtId: court.id })
      }
    }
    return map
  }, [spanKeyMap, courts])

  /** Expand a list of cells so that any cell in a merged group pulls in all group members. */
  const expandSpanGroups = (cells: { hour: number; courtId: string }[]) => {
    const result = new Map<string, { hour: number; courtId: string }>()
    for (const cell of cells) {
      const spanKey = spanKeyMap.get(`${cell.hour}-${cell.courtId}`)
      const group = spanKey ? (spanGroupCells.get(spanKey) ?? [cell]) : [cell]
      for (const c of group) {
        result.set(`${c.hour}-${c.courtId}`, c)
      }
    }
    return [...result.values()]
  }

  const columns = useMemo<GridColDef[]>(() => {
    const hourCol: GridColDef = {
      field: 'hour',
      headerName: '时段',
      width: 80,
      sortable: false,
      pinnable: false,
      align: 'center',
      headerAlign: 'center',
    }
    const courtCols: GridColDef[] = courts.map((court) => ({
      field: court.id,
      headerName: court.name,
      width: 100,
      sortable: false,
      pinnable: false,
      align: 'center',
      headerAlign: 'center',
      rowSpanValueGetter: (_value: unknown, row: { id: number }) => {
        return spanKeyMap.get(`${row.id}-${court.id}`) ?? `${row.id}-${court.id}`
      },
      renderCell: (params) => {
        const hour = params.id as number
        const meta = slotLookup.get(`${hour}-${court.id}`)
        if (!meta) return '—'

        // If slot has a reason, wrap entire cell with tooltip
        if (meta.reason) {
          return (
            <Tooltip
              title={meta.reason}
              arrow
              placement="top"
              slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
            >
              <Box sx={{ width: '100%' }}>
                <Box sx={{ lineHeight: 'normal' }}>¥{meta.price}</Box>
                <Box
                  sx={{
                    fontSize: '0.7rem',
                    color: '#e65100',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 'normal',
                  }}
                >
                  {meta.reason.split('\n')[0]}
                </Box>
              </Box>
            </Tooltip>
          )
        }

        // Otherwise, just show price
        return (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Box>¥{meta.price}</Box>
          </Box>
        )
      },
    }))
    return [hourCol, ...courtCols]
  }, [courts, slotLookup, spanKeyMap])

  const rows = useMemo(() => {
    return HOURS.map((hour) => ({
      id: hour,
      hour: formatHour(hour),
    }))
  }, [])

  // Hours before this threshold are "past" and non-selectable
  // Past date → 24 (all past), today → current China hour + 1, future → 0 (none past)
  // At 14:06, current hour is 14, threshold is 15, so hour 14 is past (slot has started)
  const pastHourThreshold = useMemo(() => {
    if (!selectedDate) return 0
    const chinaToday = getChinaToday()
    if (isBefore(selectedDate, chinaToday)) return 24
    if (
      isToday(chinaToday) &&
      format(selectedDate, 'yyyy-MM-dd') === format(chinaToday, 'yyyy-MM-dd')
    )
      return getChinaHour() + 1
    return 0
  }, [selectedDate])

  // --- Drag selection ---
  const [selectedCells, setSelectedCells] = useState<
    { hour: number; courtId: string }[]
  >([])
  const courtIds = useMemo(() => courts.map((c) => c.id), [courts])
  const gridRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const dragStart = useRef<{ hour: number; courtId: string } | null>(null)
  const isCtrlDrag = useRef(false)
  const pastRef = useRef(pastHourThreshold)
  pastRef.current = pastHourThreshold

  const selectedKeys = useMemo(
    () => new Set(selectedCells.map((c) => `${c.hour}-${c.courtId}`)),
    [selectedCells],
  )

  const clearHighlight = () => {
    gridRef.current?.querySelectorAll(`.${DRAG_HIGHLIGHT}`).forEach((el) => {
      el.classList.remove(DRAG_HIGHLIGHT)
    })
  }

  const highlightRect = (
    start: { hour: number; courtId: string },
    end: { hour: number; courtId: string },
  ) => {
    clearHighlight()
    for (const { hour, courtId } of getCellRect(start, end, courtIds)) {
      if (hour < pastRef.current) continue
      const row = gridRef.current?.querySelector(`[data-id="${hour}"]`)
      row?.querySelector(`[data-field="${courtId}"]`)?.classList.add(DRAG_HIGHLIGHT)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    const info = getCellInfo(e.target as HTMLElement)
    if (!info || info.hour < pastRef.current) {
      if (!e.ctrlKey) setSelectedCells([])
      return
    }
    isDragging.current = true
    dragStart.current = info
    isCtrlDrag.current = e.ctrlKey
    clearHighlight()
    highlightRect(info, info)
    e.preventDefault()
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !dragStart.current) return
    const info = getCellInfo(e.target as HTMLElement)
    if (!info) return
    highlightRect(dragStart.current, info)
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging.current || !dragStart.current) return
    isDragging.current = false
    const end = getCellInfo(e.target as HTMLElement) || dragStart.current
    const cells = expandSpanGroups(
      getCellRect(dragStart.current, end, courtIds).filter(
        (c) => c.hour >= pastRef.current && slotLookup.has(`${c.hour}-${c.courtId}`),
      ),
    )
    dragStart.current = null
    clearHighlight()
    if (isCtrlDrag.current) {
      // Toggle: add new cells, remove already-selected ones
      const newKeys = new Set(cells.map((c) => `${c.hour}-${c.courtId}`))
      setSelectedCells((prev) => {
        const removed = prev.filter((c) => !newKeys.has(`${c.hour}-${c.courtId}`))
        const added = cells.filter((c) => !selectedKeys.has(`${c.hour}-${c.courtId}`))
        return [...removed, ...added]
      })
    } else {
      setSelectedCells(cells)
    }
  }

  // Derive actionable cells from selection
  // Event-reserved slots (eventId set) are excluded from actions, just like booked slots
  const { hasOpen, hasReserved } = useMemo(() => {
    let open = false
    let reserved = false
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (!meta) continue
      if (meta.status === SlotStatus.Open) open = true
      if (meta.status === SlotStatus.Reserved && !meta.eventId) reserved = true
    }
    return { hasOpen: open, hasReserved: reserved }
  }, [selectedCells, slotLookup])

  // Mutation
  const [overrideSlots, { loading: mutating }] = useMutation(OverrideSlotsDocument)

  // Order dialog
  const [orderDialogId, setOrderDialogId] = useState<string | null>(null)

  // Event dialog
  const [eventDialogId, setEventDialogId] = useState<string | null>(null)

  // Reason dialog
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [reasonInput, setReasonInput] = useState('')

  // Price dialog
  const [priceDialogOpen, setPriceDialogOpen] = useState(false)
  const [priceInput, setPriceInput] = useState('')

  const refetchSlots = () => {
    if (!currentSite || !selectedDate) return
    return slotsRefetch()
  }

  const handleReserveOpen = () => {
    setReasonInput('')
    setReasonDialogOpen(true)
  }

  const handleReserveConfirm = async () => {
    const inputs = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (meta?.status !== SlotStatus.Open) continue
      inputs.push({
        courtId: cell.courtId,
        startTime: meta.startTime,
        status: SlotStatus.Reserved,
        reason: reasonInput || undefined,
      })
    }
    if (inputs.length === 0) return
    setReasonDialogOpen(false)
    await overrideSlots({ variables: { slots: inputs } })
    setSelectedCells([])
    await refetchSlots()
  }

  const handleRelease = async () => {
    const inputs = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (meta?.status !== SlotStatus.Reserved || meta.eventId) continue
      inputs.push({
        courtId: cell.courtId,
        startTime: meta.startTime,
        status: SlotStatus.Open,
      })
    }
    if (inputs.length === 0) return
    await overrideSlots({ variables: { slots: inputs } })
    setSelectedCells([])
    refetchSlots()
  }

  const handleChangePriceOpen = () => {
    setPriceInput('')
    setPriceDialogOpen(true)
  }

  const handleChangePriceConfirm = async () => {
    if (priceInput === '' || Number(priceInput) < 0 || Number(priceInput) > 999) return
    const price = priceInput
    const inputs = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (
        !meta ||
        (meta.status !== SlotStatus.Open && meta.status !== SlotStatus.Reserved) ||
        (meta.status === SlotStatus.Reserved && meta.eventId)
      )
        continue
      inputs.push({ courtId: cell.courtId, startTime: meta.startTime, price })
    }
    if (inputs.length === 0) return
    setPriceDialogOpen(false)
    await overrideSlots({ variables: { slots: inputs } })
    setSelectedCells([])
    refetchSlots()
  }

  const loading = sitesLoading || slotsLoading
  const error = sitesError || slotsError

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 48px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h4">场地管理</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="前一天">
            <span>
              <IconButton
                size="small"
                onClick={handlePrevDay}
                disabled={isPrevDayDisabled}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <DatePicker
            value={inputDate}
            onChange={handleDateChange}
            onAccept={handleDateAccept}
            format="yyyy-MM-dd E"
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{
              textField: {
                size: 'small',
                onKeyDown: handleKeyDown,
                sx: { width: 220 },
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title="今天">
                        <IconButton
                          size="small"
                          onClick={handleTodayClick}
                          edge="start"
                          aria-label="go to today"
                        >
                          <MyLocationIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              },
              actionBar: {
                actions: [],
                sx: {
                  '& .MuiButton-root': {
                    border: '1px solid',
                    borderColor: 'primary.main',
                  },
                },
              },
            }}
          />
          <Tooltip title="后一天">
            <span>
              <IconButton
                size="small"
                onClick={handleNextDay}
                disabled={isNextDayDisabled}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Action buttons and legend */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasOpen || mutating}
            onClick={handleReserveOpen}
          >
            保留
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasReserved || mutating}
            onClick={handleRelease}
          >
            释放
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasOpen || mutating}
            onClick={handleChangePriceOpen}
          >
            改价
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
          {STATUS_LEGEND.map((item) => (
            <Box
              key={item.status}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '2px',
                  backgroundColor: item.bg,
                  border: '1px solid #ccc',
                }}
              />
              <Typography variant="caption" sx={{ color: item.color }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {error && <Alert severity="error">加载预约数据失败: {error.message}</Alert>}

      <Box
        ref={gridRef}
        sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DataGridPro
          rows={rows}
          columns={columns}
          hideFooter
          disableRowSelectionOnClick
          disableColumnMenu
          density="compact"
          rowSpanning
          rowHeight={52}
          onCellClick={(params) => {
            if (params.field === 'hour') return
            const meta = slotLookup.get(`${params.id}-${params.field}`)
            if (meta?.status === SlotStatus.Booked && meta.orderId) {
              setOrderDialogId(meta.orderId)
            } else if (meta?.eventId) {
              setEventDialogId(meta.eventId)
            }
          }}
          getCellClassName={(params) => {
            if (params.field === 'hour') return ''
            const hour = params.id as number
            const key = `${hour}-${params.field}`
            const meta = slotLookup.get(key)
            const classes: string[] = []
            if (hour < pastHourThreshold) {
              classes.push('past-cell')
            } else if (!meta) {
              classes.push('unavailable-cell')
            } else {
              if (meta.status === SlotStatus.Reserved) classes.push('status-reserved')
              else if (meta.status === SlotStatus.Booked) classes.push('status-booked')
              else if (meta.status === SlotStatus.Locked) classes.push('status-locked')
            }
            if (selectedKeys.has(key)) classes.push('cell-selected')
            return classes.join(' ')
          }}
          sx={{
            height: '100%',
            userSelect: 'none',
            '& .MuiDataGrid-cell': {
              borderRight: '1px solid rgba(224, 224, 224, 1)',
              cursor: 'cell',
              display: 'flex',
              alignItems: 'center',
              py: 1,
            },
            '& .MuiDataGrid-columnHeader': {
              borderRight: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .unavailable-cell': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: 'rgba(0, 0, 0, 0.38)',
              cursor: 'default',
            },
            '& .past-cell': {
              backgroundColor: '#f5f5f5',
              color: 'rgba(0, 0, 0, 0.38)',
              cursor: 'default',
            },
            '& .status-reserved': {
              backgroundColor: '#fff3e0',
              color: '#e65100',
            },
            '& .status-booked': {
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
            },
            '& .status-locked': {
              backgroundColor: '#e0e0e0',
              color: '#424242',
            },
            '& .cell-selected': {
              outline: '2px solid #1976d2',
              outlineOffset: '-2px',
            },
            [`& .${DRAG_HIGHLIGHT}`]: {
              backgroundColor: 'rgba(25, 118, 210, 0.16) !important',
            },
            // Restore right border on cells covered by row spanning (rendered as role="presentation" divs)
            '& [role="presentation"][data-colindex]': {
              borderRight: '1px solid rgba(224, 224, 224, 1)',
            },
          }}
          initialState={{
            pinnedColumns: { left: ['hour'] },
          }}
        />
      </Box>

      <Dialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>保留原因</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="原因"
            fullWidth
            multiline
            rows={3}
            value={reasonInput}
            onChange={(e) => setReasonInput(e.target.value.slice(0, 100))}
            slotProps={{
              htmlInput: { maxLength: 100 },
            }}
            helperText={`${reasonInput.length}/100`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialogOpen(false)}>取消</Button>
          <Button onClick={handleReserveConfirm} variant="contained">
            确认
          </Button>
        </DialogActions>
      </Dialog>

      <OrderDialog orderId={orderDialogId} onClose={() => setOrderDialogId(null)} />

      <EventDialog eventId={eventDialogId} onClose={() => setEventDialogId(null)} />

      <Dialog
        open={priceDialogOpen}
        onClose={() => setPriceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>修改价格</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="新价格 (元)"
            type="number"
            fullWidth
            value={priceInput}
            onChange={(e) => {
              const v = e.target.value
              if (v.includes('-')) return
              if (v === '' || (/^\d{0,3}(\.\d{0,2})?$/.test(v) && Number(v) <= 999)) {
                setPriceInput(v)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'Minus') {
                e.preventDefault()
              }
            }}
            slotProps={{
              htmlInput: { min: 0, max: 999, step: 0.01 },
            }}
            sx={{
              mt: 1,
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleChangePriceConfirm}
            variant="contained"
            disabled={
              priceInput === '' || Number(priceInput) < 0 || Number(priceInput) > 999
            }
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
